import {
  useQueries, useQuery, useMutation, useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { cashAppLocal } from '@squareup/dashboard-query/queries/cash-app-local';
import { fulfillmentConfiguration } from '@squareup/dashboard-query/queries/fulfillment-configuration';
import { useErrorTracking } from '@squareup/service-error-tracking';
import { useSession } from '@squareup/service-session';
import { EnablementStatus } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/enums.js';
import {
  EntityIdentifier, FulfillmentConfiguration,
} from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models.js';
import { useCashAppDefaultBrand } from './useCashApp.js';

// Location ID => intended state for enablement to be updated to
export interface LocationFulfillmentEnablementUpdates {
  [key: string]: boolean;
}

// Used to differentiate between pickup and delivery flows
export enum FulfillmentType {
  Pickup = 'pickup',
  Delivery = 'delivery',
}

export function useFulfillmentConfigurationForAllLocations(locationIds: string[]) {
  const { merchantToken } = useSession();
  const { data: defaultBrandData } = useQuery(cashAppLocal.defaultBrand());
  const channelId = defaultBrandData?.brandChannelId;

  const queriesConfig = locationIds.map((locationId) => ({
    ...fulfillmentConfiguration.readEntity({
      entity: EntityIdentifier.create({
        merchantId: merchantToken,
        locationId,
        channelId,
      }),
    }),
    enabled: Boolean(locationId && merchantToken && channelId),
  }));

  return useQueries({ queries: queriesConfig });
}

export function useLocationIdToFulfillmentConfigurationMap(locationIds: string[]) {
  const results = useFulfillmentConfigurationForAllLocations(locationIds);
  return useMemo(() => {
    const isLoading = results.some((queryResult) => queryResult.isLoading);
    if (isLoading) {
      return {};
    }
    return results.reduce((acc, config) => {
      if (config.data?.configuration?.entity?.locationId) {
        acc[config.data.configuration.entity?.locationId] = config.data.configuration;
      }
      return acc;
    }, {} as Record<string, FulfillmentConfiguration>);
  }, [results]);
}

export function useUpsertFulfillmentConfiguration() {
  const queryClient = useQueryClient();
  const { merchantToken } = useSession();
  const { data: defaultBrandData } = useCashAppDefaultBrand();
  const channelId = defaultBrandData?.brandChannelId;
  const { captureException } = useErrorTracking();

  return useMutation({
    ...fulfillmentConfiguration.upsertEntity(),
    onSuccess: (data) => {
      const locationId = data.configuration?.entity?.locationId;
      if (locationId && merchantToken && channelId) {
        const fulfillmentQueryKey = fulfillmentConfiguration.readEntity({
          entity: EntityIdentifier.create({ merchantId: merchantToken, locationId, channelId }),
        }).queryKey;
        const currentCache = queryClient.getQueryData(fulfillmentQueryKey);

        const transformedData = {
          ...(currentCache || {}),
          configuration: data.configuration,
        };

        queryClient.setQueryData(
          fulfillmentQueryKey,
          transformedData,
        );
      } else {
        queryClient.invalidateQueries({ queryKey: fulfillmentConfiguration.keys.root() });
      }
    },
    onError: (error) => {
      captureException(new Error('Error upserting fulfillment configuration'), {
        captureContext: {
          extra: {
            error,
          },
        },
      });
    },
  });
}

/**
 * Hook that provides functions to update fulfillment enablement for both pickup and delivery
 */
export function useFulfillmentEnablementUpdater() {
  const { merchantToken } = useSession();
  const { data: defaultBrandData } = useCashAppDefaultBrand();
  const channelId = defaultBrandData?.brandChannelId;
  const upsertMutation = useUpsertFulfillmentConfiguration();

  // Update fulfillment enablement for a single location
  const updateLocationFulfillmentEnabled = async (
    fulfillmentType: FulfillmentType,
    enabled: boolean,
    existingConfig?: FulfillmentConfiguration,
  ) => {
    if (!merchantToken || !channelId) {
      throw new Error('Missing required session data');
    }

    const updatedEnablementStatus = enabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;

    const config = FulfillmentConfiguration.create({
      ...existingConfig,
      pickupSettings: fulfillmentType === FulfillmentType.Pickup
        ? { ...existingConfig?.pickupSettings, enablementStatus: updatedEnablementStatus }
        : existingConfig?.pickupSettings,
      deliverySettings: fulfillmentType === FulfillmentType.Delivery
        ? { ...existingConfig?.deliverySettings, enablementStatus: updatedEnablementStatus }
        : existingConfig?.deliverySettings,
    });

    const response = await upsertMutation.mutateAsync({ configuration: config });
    if (response.errors.length > 0) {
      throw new Error('Error upserting fulfillment configuration');
    }
    return response;
  };

  // Update fulfillment enablement for multiple locations
  const updateLocationsFulfillmentEnabled = async (
    fulfillmentType: FulfillmentType,
    locationsToUpdate: LocationFulfillmentEnablementUpdates,
    locationIdToConfigMap?: Record<string, FulfillmentConfiguration>,
  ) => {
    const locationUpdateArray = Object.entries(locationsToUpdate);
    if (locationUpdateArray.length === 0) {
      return [];
    }
    const updates = locationUpdateArray.map(([locationId, enabled]) => updateLocationFulfillmentEnabled(
      fulfillmentType,
      enabled,
      locationIdToConfigMap?.[locationId],
    ));
    return Promise.all(updates);
  };

  return {
    updateLocationFulfillmentEnabled,
    updateLocationsFulfillmentEnabled,
  };
}
